import {
  createInternalComponent,
  FormGenerator,
  useFeedback,
  useTranslation,
} from "@opendash/core";
import { useDataItem } from "@opendash/plugin-timeseries";
import * as React from "react";
import { AlarmInterface, useMonitoringService } from "..";
import { AlarmCondition } from "./AlarmCondition"; // Wichtiger Import!
interface Props {
  alarm: AlarmInterface;
  onClose: () => void;
  defaultEmail?: string;
  userService?: any;
}

// Erweiterte Hilfsfunktion
function normalizeAlarm(
  a: AlarmInterface,
  monitoringService: ReturnType<typeof useMonitoringService>
): AlarmInterface {
  let normalized = { ...a };

  // Trigger-Zeit normalisieren
  if (
    ["ts_last_value_changed", "ts_last_value_received"].includes(
      a.trigger.type
    ) && // @ts-ignore
    typeof a.trigger.time === "object" && // @ts-ignore
    a.trigger.time !== null && // @ts-ignore
    "value" in a.trigger.time
  ) {
    // @ts-ignore
    const ms = (a.trigger.time as { value: number }).value; // @ts-ignore
    normalized.trigger = { ...normalized.trigger, time: ms / 1000 / 60 };
  }

  // Aktionsfelder normalisieren (Werte aus initialAlarm.action in präfixierte Schlüssel mappen)
  if (a.action && a.action.actionId) {
    const selectedActionDefinition = monitoringService
      .listAlarmActions()
      .find((act) => act.id === a.action.actionId);

    if (
      selectedActionDefinition &&
      Array.isArray(selectedActionDefinition.formFields)
    ) {
      const newActionState = { actionId: a.action.actionId };
      selectedActionDefinition.formFields.forEach((fieldConfig) => {
        const stateKeyForField = `${selectedActionDefinition.id}__${fieldConfig.key}`;
        // Wert aus dem ursprünglichen Action-Objekt holen (entweder unter data-key oder key)
        const valueFromInitial = fieldConfig["data-key"]
          ? a.action[fieldConfig["data-key"]]
          : a.action[fieldConfig.key];

        if (valueFromInitial !== undefined) {
          newActionState[stateKeyForField] = valueFromInitial;
        }
      });
      normalized.action = newActionState;
    } else {
      // Fallback, falls keine formFields definiert sind, nur actionId behalten
      normalized.action = { actionId: a.action.actionId };
    }
  } else {
    // Fallback, falls keine actionId vorhanden ist
    normalized.action = { actionId: undefined };
  }

  return normalized;
}

export const AlarmEditModal = createInternalComponent<Props>(
  function AlarmEditModal({ alarm: initialAlarm, onClose }) {
    const t = useTranslation();
    const MonitoringService = useMonitoringService();
    const { message } = useFeedback();

    const item = useDataItem(initialAlarm.item[0], initialAlarm.item[1]);
    const dimension = initialAlarm.item[2] || 0;

    const [alarm, setAlarm] = React.useState<AlarmInterface>(() =>
      normalizeAlarm(initialAlarm, MonitoringService)
    );

    React.useEffect(() => {
      setAlarm(normalizeAlarm(initialAlarm, MonitoringService));
    }, [initialAlarm, MonitoringService]); // MonitoringService zur Dependency List hinzugefügt

    const alarmKey = initialAlarm.id || JSON.stringify(initialAlarm.item); // Sicherstellen, dass alarmKey eindeutig ist

    return (
      <React.Fragment key={alarmKey}>
        <p>
          <b>{t("opendash:monitoring.alarms.trigger.description")}</b>
        </p>

        <FormGenerator
          key={`trigger-${alarmKey}`}
          state={alarm.trigger}
          updateState={(k, v) =>
            setAlarm((o) => ({ ...o, trigger: { ...o.trigger, [k]: v } }))
          }
          settings={{ removeHidden: true }}
          elements={[
            {
              key: "type",
              type: "select",
              label: t("opendash:data.trigger.select_type_placeholder"),
              defaultValue: alarm.trigger.type,
              settings: {
                options: MonitoringService.triggerTypes
                  .filter(
                    (value) =>
                      value.startsWith(
                        item?.valueTypes[dimension].type?.toLowerCase()
                      ) || value.startsWith("ts_")
                  )
                  .map((type) => ({
                    value: type,
                    label: t("opendash:data.trigger." + type),
                  })),
              },
            },
            {
              key: "value",
              type: "input.number",
              label: t("opendash:data.trigger.select_value_placeholder"),
              defaultValue: 0,
              visible: (state) =>
                [
                  "number_equals",
                  "number_equals_not",
                  "number_gt",
                  "number_lt",
                ].includes(state.type),
            },
            {
              key: "min",
              type: "input.number",
              label: t("opendash:data.trigger.select_min_placeholder"),
              defaultValue: 0,
              visible: (state) =>
                ["number_in_range", "number_out_of_range"].includes(state.type),
            },
            {
              key: "max",
              type: "input.number",
              label: t("opendash:data.trigger.select_max_placeholder"),
              defaultValue: 100,
              visible: (state) =>
                ["number_in_range", "number_out_of_range"].includes(state.type),
            },
            {
              key: "string",
              type: "input",
              label: t("opendash:data.trigger.select_string_placeholder"),
              defaultValue: "",
              visible: (state) =>
                [
                  "string_equals",
                  "string_equals_not",
                  "string_includes",
                  "string_includes_not",
                  "string_starts_with",
                  "string_starts_with_not",
                  "string_ends_with",
                  "string_ends_with_not",
                ].includes(state.type),
            },

            {
              key: "time",
              type: "input.number",
              label: t("opendash:data.trigger.select_ts_placeholder"),
              defaultValue: (() => {
                //@ts-ignore
                const tval = alarm.trigger.time;
                // wenn Objekt, hole time.value und wandle in Minuten
                if (typeof tval === "object" && tval !== null) {
                  return (tval?.value as number) / (1000 * 60);
                }
                // ansonsten Zahl oder Fallback 60
                return typeof tval === "number" ? tval : 60;
              })(),
              visible: (state) =>
                ["ts_last_value_changed", "ts_last_value_received"].includes(
                  state.type
                ),
            },

            {
              key: "interval",
              type: "select",
              label: t("opendash:data.trigger.select_interval_placeholder"),
              defaultValue: 0,
              settings: {
                options: [
                  {
                    label: t(
                      "opendash:monitoring.alarms.trigger.interval_everytime"
                    ),
                    value: 0,
                  },
                  {
                    label: t(
                      "opendash:monitoring.alarms.trigger.interval_once_per_hour"
                    ),
                    value: 1000 * 60 * 60,
                  },
                  {
                    label: t(
                      "opendash:monitoring.alarms.trigger.interval_once_per_day"
                    ),
                    value: 1000 * 60 * 60 * 24,
                  },
                  {
                    label: t(
                      "opendash:monitoring.alarms.trigger.interval_once_per_week"
                    ),
                    value: 1000 * 60 * 60 * 24 * 7,
                  },
                  {
                    label: t(
                      "opendash:monitoring.alarms.trigger.interval_once_per_month"
                    ),
                    value: 1000 * 60 * 60 * 24 * 7 * 4,
                  },
                ],
              },
            },
          ]}
        />

        <p>
          <b>{t("opendash:monitoring.alarms.condition.description")}</b>
        </p>

        <AlarmCondition
          triggerItem={[item.source, item.id, dimension]}
          value={alarm.condition}
          onChange={(condition) => {
            setAlarm((old) => ({ ...old, condition }));
          }}
        />

        <p>
          <b>{t("opendash:monitoring.alarms.action.description")}</b>
        </p>

        <FormGenerator
          key={`action-${alarmKey}`}
          state={alarm.action}
          updateState={(k, v) =>
            setAlarm((o) => ({ ...o, action: { ...o.action, [k]: v } }))
          }
          settings={{
            removeHidden: true,
          }}
          submit={{
            children: t("opendash:monitoring.alarms.edit.submit"),
            onClick: () => {
              const payload = JSON.parse(JSON.stringify(alarm));

              // Zeitwerte umrechnen
              if (
                payload.trigger.type === "ts_last_value_changed" ||
                payload.trigger.type === "ts_last_value_received"
              ) {
                payload.trigger.time = {
                  value: Number(payload.trigger.time) * 1000 * 60,
                };
              }

              const selectedActionDefinition =
                MonitoringService.listAlarmActions().find(
                  (act) => act.id === alarm.action.actionId
                );

              const finalActionPayload = { actionId: alarm.action.actionId };

              if (
                selectedActionDefinition &&
                Array.isArray(selectedActionDefinition.formFields)
              ) {
                selectedActionDefinition.formFields.forEach(
                  (originalFieldConfig) => {
                    const stateKeyForField = `${selectedActionDefinition.id}__${originalFieldConfig.key}`;
                    const valueFromState = alarm.action[stateKeyForField];

                    if (valueFromState !== undefined) {
                      if (originalFieldConfig["data-key"]) {
                        finalActionPayload[originalFieldConfig["data-key"]] =
                          valueFromState;
                      } else {
                        finalActionPayload[originalFieldConfig.key] =
                          valueFromState;
                      }
                    }
                  }
                );
              }
              payload.action = finalActionPayload;

              MonitoringService.updateAlarm(payload).then(
                () => {
                  message.success(
                    t("opendash:monitoring.alarms.create.success") // Ggf. eigene Übersetzung für Update verwenden
                  );
                  onClose();
                },
                (error) => {
                  // Fehlerbehandlung hinzugefügt
                  console.error("Fehler beim Aktualisieren des Alarms:", error);
                  message.error(t("opendash:monitoring.alarms.create.error")); // Ggf. eigene Übersetzung für Update verwenden
                }
              );
            },
          }}
          elements={[
            {
              key: "actionId",
              type: "select",
              label: t("opendash:monitoring.alarms.action.select"),
              defaultValue: alarm.action.actionId, // Statt direkter Filterung
              settings: {
                options: MonitoringService.listAlarmActions()
                  .filter((c) =>
                    c.supportedTypes
                      .map((x) => x.toLowerCase())
                      .includes(item.valueTypes[dimension].type.toLowerCase())
                  )
                  .map((action) => ({
                    value: action.id,
                    label: action.label,
                  })),
              },
            },

            ...MonitoringService.listAlarmActions().flatMap((action) => {
              if (!Array.isArray(action.formFields)) {
                return [];
              }

              return action.formFields.map((originalField) => {
                const uniqueKeyForFormElement = `${action.id}__${originalField.key}`;
                return {
                  ...originalField,
                  key: uniqueKeyForFormElement,
                  visible: (state) => state.actionId === action.id,
                };
              });
            }),
          ]}
        />
      </React.Fragment>
    );
  }
);
